import React, { useState, useCallback, useMemo } from "react";
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Stack,
  Tooltip,
  useTheme,
  Box,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import JsonObjectViewer from "../../../../LowLevelComponents/JsonObjectViewer";
import CopyToClipboard from "../../../../../CopyToClipboard";

interface CellMetadataModalProps {
  params: { value?: Record<string, unknown>; row?: Record<string, unknown> };
  children?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  /**
   * Optional row-level field names to surface as chips next to the modal title
   * (field + value). Useful when the row carries contextual fields (e.g.
   * `reason_code`, `issue`) that aren't part of the `metadata` object itself.
   */
  headerChipFields?: string[];
}

const humanizeFieldLabel = (key: string): string =>
  key
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());

const formatChipValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) {
    return value.length ? value.map(v => String(v)).join(", ") : "—";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const parseJsonLikeValue = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return value;
  }

  const mayBeJson =
    (trimmedValue.startsWith("{") && trimmedValue.endsWith("}")) ||
    (trimmedValue.startsWith("[") && trimmedValue.endsWith("]"));

  if (!mayBeJson) {
    return value;
  }

  try {
    return JSON.parse(trimmedValue);
  } catch {
    return value;
  }
};

const isObjectOrArrayValue = (value: unknown): value is Record<string, unknown> | unknown[] => {
  if (value === null || value === undefined) {
    return false;
  }

  if (Array.isArray(value)) {
    return true;
  }

  return typeof value === "object";
};

const formatMetadataValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  return String(value);
};

const formatMetadataValueForCopy = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const isEmptyValueForCopy = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim().length === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === "object") {
    return Object.keys(value as Record<string, unknown>).length === 0;
  }

  return false;
};

const CellMetadataModal = ({
  params,
  onClick,
  headerChipFields,
}: CellMetadataModalProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const metadata = (params?.value && typeof params.value === "object"
    ? params.value
    : params?.row?.metadata) as Record<string, unknown> | null | undefined;

  const headerChips = useMemo(() => {
    if (!Array.isArray(headerChipFields) || headerChipFields.length === 0) return [];
    const row = (params?.row ?? {}) as Record<string, unknown>;
    return headerChipFields
      .map(field => ({ field, value: row[field] }))
      .filter(({ value }) => value !== undefined && value !== null && value !== "");
  }, [headerChipFields, params?.row]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (onClick) onClick(e);
      if (metadata && Object.keys(metadata).length > 0) {
        setOpen(true);
      }
    },
    [metadata, onClick]
  );

  const handleClose = useCallback(() => setOpen(false), []);

  const hasMetadata = metadata && typeof metadata === "object" && Object.keys(metadata).length > 0;
  const entries = hasMetadata ? Object.entries(metadata as Record<string, unknown>) : [];

  return (
    <>
      <Box
        component="span"
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: hasMetadata ? "pointer" : "default",
          color: hasMetadata ? theme.palette.primary.main : theme.palette.text.secondary,
          "&:hover": hasMetadata
            ? { textDecoration: "underline" }
            : {},
        }}
        aria-label="View metadata"
      >
        <VisibilityIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2">
          {hasMetadata ? "View" : "—"}
        </Typography>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "fit-content",
            maxWidth: "95vw",
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <span>Metadata</span>
          {headerChips.length > 0 && (
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
              sx={{ alignItems: "center" }}
            >
              {headerChips.map(({ field, value }) => {
                const valueText = formatChipValue(value);
                const label = `${humanizeFieldLabel(field)}: ${valueText}`;
                return (
                  <Tooltip key={field} title={label} arrow>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      label={label}
                      sx={{ maxWidth: 320, fontWeight: 500 }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          )}
        </DialogTitle>
        <DialogContent>
          {entries.length > 0 ? (
            <Box component="dl" sx={{ m: 0 }}>
              {entries.map(([key, value]) => {
                const parsedValue = parseJsonLikeValue(value);
                const shouldRenderJson = isObjectOrArrayValue(parsedValue);
                const disableCopy = isEmptyValueForCopy(parsedValue);

                return (
                  <Box
                    key={key}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "140px 1fr",
                      gap: 1,
                      py: 1,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Typography
                      component="dt"
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        textTransform: "capitalize",
                      }}
                    >
                      {key.replace(/_/g, " ")}:
                    </Typography>
                    <Box
                      component="dd"
                      sx={{
                        m: 0,
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        {shouldRenderJson ? (
                          <JsonObjectViewer
                            data={parsedValue}
                            defaultExpandedDepth={2}
                            defaultWidth={560}
                            defaultHeight="auto"
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: "break-word",
                              color: theme.palette.text.primary,
                            }}
                          >
                            {formatMetadataValue(parsedValue)}
                          </Typography>
                        )}
                      </Box>
                      <CopyToClipboard
                        text={formatMetadataValueForCopy(parsedValue)}
                        disabled={disableCopy}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No metadata available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CellMetadataModal;
